
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { TestResponse, RecruitmentStatus } from '@/types/test-response';

export function useBulkActions() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const bulkUpdateStatus = useMutation({
    mutationFn: async ({ 
      responseIds, 
      status 
    }: { 
      responseIds: string[]; 
      status: RecruitmentStatus; 
    }) => {
      const { data, error } = await supabase
        .from('test_responses')
        .update({ recruitment_status: status })
        .in('id', responseIds)
        .select('id, recruitment_status');

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Bulk update successful",
        description: `Updated ${variables.responseIds.length} responses to ${variables.status}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['test-responses'] });
    },
    onError: (error: any) => {
      toast({
        title: "Bulk update failed",
        description: error.message || "An error occurred during bulk update.",
        variant: "destructive",
      });
    },
  });

  const exportToCSV = (responses: TestResponse[]) => {
    const headers = ['Candidate', 'Email', 'Status', 'Date', 'AI Score', 'Integrity', 'Recruitment', 'Notes'];
    
    const csvData = responses.map(response => {
      return [
        response.candidate.full_name || '',
        response.candidate.email || '',
        response.status || '',
        new Date(response.started_at).toLocaleDateString(),
        response.total_ai_score ? `${Math.round(response.total_ai_score)}%` : '-',
        response.integrity_score ? `${response.integrity_score}%` : '-',
        response.recruitment_status || 'pending',
        response.recruitment_notes || '',
      ];
    });

    const csvContent = [headers, ...csvData]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `test-responses-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    toast({
      title: "Export successful",
      description: `Exported ${responses.length} responses to CSV.`,
    });
  };

  return {
    bulkUpdateStatus,
    exportToCSV,
    isUpdating: bulkUpdateStatus.isPending,
  };
}
